export const APP_TIME_ZONE = "America/Manaus";

const CLOCK_DRIFT_TOLERANCE_MS = 5 * 60 * 1000;
let serverClockOffsetMs = 0;

export const synchronizeAppClock = (serverDateHeader?: string | null, clientReceivedAt = Date.now()) => {
  if (!serverDateHeader) return;
  const serverTime = Date.parse(serverDateHeader);
  if (!Number.isFinite(serverTime)) return;
  const drift = serverTime - clientReceivedAt;
  // HTTP Date has second precision. Ignore small differences to avoid jitter near midnight.
  serverClockOffsetMs = Math.abs(drift) > CLOCK_DRIFT_TOLERANCE_MS ? drift : 0;
};

export const appNow = (clientNow = Date.now()) => new Date(clientNow + serverClockOffsetMs);

export const resetAppClock = () => {
  serverClockOffsetMs = 0;
};

const CIVIL_DATE_FIELDS = new Set([
  "data_nascimento",
  "data_admissao",
  "data_vencimento",
  "data_pagamento",
  "data_recebimento",
  "data_movimentacao",
  "data_emissao",
  "data_prescricao",
  "data_abertura",
  "data_prevista",
  "data_compensacao",
  "data_inicio",
  "data_fim",
  "data_conclusao",
  "data_retorno",
  "data_envio",
  "data_foto",
  "data_exame",
  "data_aquisicao",
  "data_contrato",
  "validade",
  "competencia_inicio",
  "competencia_fim",
  "periodo_inicio",
  "periodo_fim",
  "meta_inicio",
  "meta_fim",
  "ultima_consulta",
  "previsao_termino",
  "proxima_consulta",
  "ultima_manutencao",
  "proxima_manutencao",
]);

const datePartsInAppTimeZone = (date: Date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || "";
  return { year: value("year"), month: value("month"), day: value("day") };
};

export const todayLocalISO = () => {
  const { year, month, day } = datePartsInAppTimeZone(appNow());
  return `${year}-${month}-${day}`;
};

export const toLocalISODate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseLocalDate = (value?: string | Date | null) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  const [datePart] = String(value).split("T");
  const parts = datePart.split("-").map(Number);
  if (parts.length === 3 && parts.every(Number.isFinite)) {
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  return new Date(value);
};

export const todayLocalDate = () => parseLocalDate(todayLocalISO());

export const normalizeLocalDate = (value?: string | Date | null, fallback = "") => {
  if (!value) return fallback;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? fallback : toLocalISODate(value);

  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return fallback;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return fallback;
  return `${match[1]}-${match[2]}-${match[3]}`;
};

export const formatLocalDateBR = (value?: string | Date | null, fallback = "-") => {
  const normalized = normalizeLocalDate(value);
  if (!normalized) return fallback;
  const [year, month, day] = normalized.split("-");
  return `${day}/${month}/${year}`;
};

export const normalizeBusinessDatePayload = <T>(item: T): T => {
  if (Array.isArray(item)) {
    return item.map((value) => normalizeBusinessDatePayload(value)) as T;
  }
  if (!item || typeof item !== "object" || item instanceof Date) return item;

  return Object.fromEntries(Object.entries(item as Record<string, unknown>).map(([key, value]) => {
    if (CIVIL_DATE_FIELDS.has(key) && value !== null && value !== undefined && value !== "") {
      const normalized = normalizeLocalDate(value as string | Date);
      return [key, normalized || value];
    }
    return [key, normalizeBusinessDatePayload(value)];
  })) as T;
};

export const formatAppDateTime = (value?: string | Date | null, fallback = "-") => {
  if (!value) return fallback;
  const raw = String(value).trim();
  const manausValue = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?$/.test(raw)
    ? `${raw.replace(" ", "T")}-04:00`
    : raw;
  const parsed = value instanceof Date ? value : new Date(manausValue);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: APP_TIME_ZONE,
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsed);
};

export const nowAppISO = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(appNow());
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || "00";
  return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value("minute")}:${value("second")}-04:00`;
};

export const formatAppDate = (value?: string | Date | null, fallback = "-") => {
  if (!value) return fallback;
  const raw = String(value).trim();
  const manausValue = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?$/.test(raw)
    ? `${raw.replace(" ", "T")}-04:00`
    : raw;
  const parsed = value instanceof Date ? value : new Date(manausValue);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return new Intl.DateTimeFormat("pt-BR", { timeZone: APP_TIME_ZONE, dateStyle: "short" }).format(parsed);
};

export const startOfLocalDay = (value?: string | Date | null) => {
  const date = parseLocalDate(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const endOfLocalDay = (value?: string | Date | null) => {
  const date = parseLocalDate(value);
  date.setHours(23, 59, 59, 999);
  return date;
};

export const parseLocalDateTime = (value?: string | Date | null) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(String(value))) return new Date(value);
  const [datePart, timePart = "00:00:00"] = String(value).split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour = 0, minute = 0, second = 0] = timePart.replace("Z", "").split(":").map(Number);
  if ([year, month, day, hour, minute, second].every(Number.isFinite)) {
    return new Date(year, month - 1, day, hour, minute, second, 0);
  }
  return new Date(value);
};

export const toLocalISODateTime = (date: Date) => {
  const day = toLocalISODate(date);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${day}T${hours}:${minutes}:${seconds}`;
};

export const buildLocalDateTime = (date: Date | string, time: string) => {
  const base = typeof date === "string" ? parseLocalDate(date) : date;
  const [hour = 0, minute = 0] = time.split(":").map(Number);
  const result = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hour, minute, 0, 0);
  return toLocalISODateTime(result);
};

export const localDateKey = (value?: string | Date | null) => toLocalISODate(parseLocalDateTime(value));
